/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable jsx-a11y/anchor-has-content */
import React, { useEffect, useRef } from 'react';
import { Button, CircularProgress } from '@material-ui/core';
import { CloudDownloadOutlined } from '@material-ui/icons';
import { DocumentNode, useLazyQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';

interface DownloadFileProps<T> {
    query: DocumentNode;
    propertyKey: string;
    variables?: T;
    fileName?: string;
    dataType: string;
    extension: string;
    label?: string;
}

const DownloadFileIconButton = <T, >({
    query,
    variables,
    propertyKey,
    fileName = 'report',
    dataType,
    extension,
    label
}: DownloadFileProps<T>) => {
    const [getData, { loading, data }] = useLazyQuery(query, { fetchPolicy: 'network-only' });
    const linkRef = useRef<HTMLAnchorElement | null>(null);
    const { t } = useTranslation();
    const download = () => {
        getData({ variables });
    };

    useEffect(() => {
        if (!loading && data) {
            if (linkRef.current) {
                const linkSource = `data:application/${dataType};base64,${data[propertyKey]}`;
                const downloadLink = linkRef.current;

                downloadLink.download = `${fileName}.${extension}`;
                downloadLink.href = linkSource;
                downloadLink.target = '_blank';
                downloadLink.click();
            }
        }
    }, [loading, data, fileName, propertyKey, dataType, extension]);

    return (
        <div>
            <Button
                disabled={loading}
                onClick={download}
                variant="outlined"
                color="primary"
                startIcon={loading ? <CircularProgress size={24} /> : <CloudDownloadOutlined />}
            >
                {label || t('Export')}
            </Button>
            <a ref={linkRef}></a>
        </div>
    );
};

export default DownloadFileIconButton;

export const DownloadCsvIconButton = <T, >(props: Omit<DownloadFileProps<T>, 'dataType' | 'extension'>) => (
    <DownloadFileIconButton
        dataType='vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        extension='xlsx'
        {...props}
    />
);

export const DownloadXmlIconButton = <T, >(props: Omit<DownloadFileProps<T>, 'dataType' | 'extension'>) => (
    <DownloadFileIconButton
        dataType='xml'
        extension='xml'
        {...props}
    />
);
